import { Component } from '@verndale/core';
import { gsap } from 'gsap';
import 'waypoints/lib/noframework.waypoints';
import { camelize } from '../../helpers';

class Module extends Component {
  setupDefaults() {
    this.isDesktop = window.matchMedia('(min-width: 1280px)');
    this.isTablet = window.matchMedia('(min-width: 768px)');
    this.isMobile = !this.isDesktop.matches && !this.isTablet.matches;
    this.currentLink = 0;

    this.dom = {
      anchors: [...document.querySelectorAll('[data-anchor]')],
      container: this.el.querySelector('.sticky-navigation'),
      navContainer: this.el.querySelector('.sticky-navigation__jump-links'),
      prev: this.el.querySelector('.sticky-navigation__button.prev'),
      next: this.el.querySelector('.sticky-navigation__button.next')
    };

    this.buildJumpLinks();
  }

  addListeners() {
    this.dom.anchors.forEach((item, i) => {
      new Waypoint({
        element: item,
        offset: 200,
        handler: direction => {
          if (direction === 'down') {
            this.selectOption(i);
          } else {
            this.selectOption(i - 1);
          }
        }
      });
    });

    this.dom.next.addEventListener('click', this.nextLink.bind(this));
    this.dom.prev.addEventListener('click', this.prevLink.bind(this));
    document.addEventListener('menuClose', () => Waypoint.refreshAll());
    if (this.dom.jumpLinksList) {
      window.addEventListener('resize', this.checkScrollWidth.bind(this));
    }
  }

  buildJumpLinks() {
    if (this.dom.anchors.length) {
      this.totalLinks = this.dom.anchors.length;
      this.dom.jumpLinksList = document.createElement('ul');
      const links = this.dom.anchors
        .map(anchor => {
          anchor.id = camelize(anchor.dataset.anchor);
          return `<li role="listbox"><a href="#${anchor.id}">${anchor.dataset.anchor}</a></li>`;
        })
        .join('');

      this.dom.jumpLinksList.innerHTML = links;
      this.dom.jumpLinksList.setAttribute('role', 'listbox');
      this.dom.navContainer.appendChild(this.dom.jumpLinksList);
      this.dom.jumpLiks = [...this.dom.jumpLinksList.querySelectorAll('a')];
      this.dom.jumpLiks.forEach(link =>
        link.addEventListener('click', this.handleJumpLinkClick.bind(this))
      );

      this.dom.container.classList.add('has-jumpLinks')
      this.handleCheckInitialAnchor();

      if (this.isDesktop.matches && this.dom.jumpLinksList) {
        this.checkScrollWidth();
      }
    }
  }

  checkScrollWidth() {
    const linksWidth = this.dom.jumpLinksList.scrollWidth;
    const containerWidth = this.dom.navContainer.clientWidth;

    if (linksWidth > containerWidth) {
      this.dom.container.classList.add('is-long');
      this.dom.container.classList.remove('is-short');
    } else {
      this.dom.container.classList.remove('is-long');
      this.dom.container.classList.add('is-short');
    }

    const target = this.dom.jumpLiks[this.currentLink].getAttribute('href');
    this.scrollToJumpLink(target);
  }

  handleCheckInitialAnchor() {
    const { hash } = window.location;

    if (hash && hash !== '#hero-intro-section') {
      if (this.currentLink === 0) {
        this.dom.prev.setAttribute('disabled', 'disabled');
      } else if (this.currentLink === this.totalLinks - 1) {
        this.dom.next.setAttribute('disabled', 'disabled');
      }
      this.jumpToAnchor(hash);
    } else {
      this.toggleButtons(0);
    }
  }

  handleJumpLinkClick(e) {
    e.preventDefault();

    const targetId = e.target.getAttribute('href');
    this.jumpToAnchor(targetId, true);
  }

  jumpToAnchor(targetId, push) {
    const target = document.getElementById(targetId.replace('#', ''));

    if (target) {
      gsap.to(window, { duration: 0, scrollTo: target.offsetTop - 96 - 30 });
      this.scrollToJumpLink(targetId);

      if (push) {
        history.pushState(null, null, targetId);
      }
    }
  }

  toggleButtons(offsetLeft) {
    const screenWidth = window.innerWidth;
    const listWidth = this.dom.jumpLinksList.clientWidth;
    const remaining = listWidth - offsetLeft + (this.isMobile ? 0 : 80);

    if (remaining <= screenWidth) {
      this.dom.next.setAttribute('disabled', 'disabled');
    } else {
      this.dom.next.removeAttribute('disabled');
    }

    if (offsetLeft <= 80) {
      this.dom.prev.setAttribute('disabled', 'disabled');
    } else {
      this.dom.prev.removeAttribute('disabled');
    }
  }

  scrollToJumpLink(target) {
    const screenWidth = window.innerWidth;
    const listWidth = this.dom.jumpLinksList.clientWidth;
    const {
      clientWidth,
      offsetLeft
    } = this.dom.jumpLiks.filter(anchor => anchor.getAttribute('href') === target)[0].parentNode;
    const remaining = listWidth - offsetLeft + clientWidth;
    const isShortList = this.dom.container.classList.contains('is-short');

    if (this.isMobile) {
      gsap.to(this.dom.jumpLinksList, {
        duration: 0,
        left: -offsetLeft,
        onComplete: () => this.toggleButtons(offsetLeft)
      });
    } else if (this.isTablet.matches && (!this.isDesktop.matches || !isShortList) && ((remaining + 90) >= screenWidth)) {
      gsap.to(this.dom.jumpLinksList, {
        duration: 0,
        left: -(offsetLeft - 80),
        onComplete: () => this.toggleButtons(offsetLeft)
      });
    } else if (this.isDesktop.matches && isShortList) {
      gsap.to(this.dom.jumpLinksList, {
        duration: 0,
        left: 0,
        onComplete: () => this.toggleButtons(offsetLeft)
      });
    }
  }

  selectOption(i) {
    this.dom.jumpLiks.forEach((link, index) => {
      if (index === i) {
        link.classList.add('active');
        this.scrollToJumpLink(link.getAttribute('href'));
      } else {
        link.classList.remove('active');
      }
    });
  }

  nextLink() {
    const next = this.currentLink + 1;

    if (next >= this.totalLinks) {
      this.currentLink = this.totalLinks;
    } else {
      this.currentLink = next;
    }
    const target = this.dom.jumpLiks[this.currentLink].getAttribute('href');
    this.scrollToJumpLink(target);
  }

  prevLink() {
    const prev = this.currentLink - 1;

    if (prev <= 0) {
      this.currentLink = 0;
    } else {
      this.currentLink = prev;
    }
    const target = this.dom.jumpLiks[this.currentLink].getAttribute('href');
    this.scrollToJumpLink(target);
  }
}

export default Module;
